import deep from 'deep-get-set';
import { createAction } from 'redux-api-middleware';
import { createSelector } from 'reselect';
import qs from 'qs';

import config from '../../config';
import { buildHeaders } from '../buildAuthHeader';
import { calculatePageCount } from './utils';

// Initial state
const initialState = {
  isLoading: false,
  errorMessage: '',
  refetch: false,
  data: [],
  selectedCampaign: null,
  isSelectedCampaignLoading: false,
  selectedCampaignErrorMessage: '',
  queryParams: {},
  meta: {},
  nextGenCampaignTemplates: [],
  loadCampaignTemplatesError: false,
  hasDuplicateCampaignError: false,
  selectedCampaignId: null
};

// Action Types
// sorting
export const CAMPAIGN_LIST_SET_QUERY = 'campaignList/CAMPAIGN_LIST_SET_QUERY';
export const CAMPAIGN_LIST_COLUMN_SORT = 'campaignList/CAMPAIGN_LIST_COLUMN_SORT';
export const CAMPAIGN_SET_DEFAULT_SORTING = 'campaignList/CAMPAIGN_SET_DEFAULT_SORTING';
// pagination
export const CAMPAIGN_LIST_PAGE_SELECT = 'campaignList/CAMPAIGN_LIST_PAGE_SELECT';
export const CAMPAIGN_RECORDS_PER_PAGE_SET = 'campaignList/CAMPAIGN_RECORDS_PER_PAGE_SET';
// campaign list
export const LOAD_CAMPAIGN_LIST_SUCCESS = 'campaignList/LOAD_CAMPAIGN_LIST_SUCCESS';
export const LOAD_CAMPAIGN_LIST_FAIL = 'campaignList/LOAD_CAMPAIGN_LIST_FAIL';
export const LOAD_CAMPAIGN_LIST = 'campaignList/LOAD_CAMPAIGN_LIST';
// campaign details page
export const LOAD_CAMPAIGN_DETAILS_SUCCESS = 'campaignList/LOAD_CAMPAIGN_DETAILS_SUCCESS';
export const LOAD_CAMPAIGN_DETAILS_FAIL = 'campaignList/LOAD_CAMPAIGN_DETAILS_FAIL';
export const LOAD_CAMPAIGN_DETAILS = 'campaignList/LOAD_CAMPAIGN_DETAILS';
export const CLEAR_CAMPAIGN_DETAILS = 'campaignList/CLEAR_CAMPAIGN_DETAILS';
// add campaign modal
export const LOAD_CAMPAIGN_TEMPLATE_TYPES_SUCCESS =
  'campaignList/LOAD_CAMPAIGN_TEMPLATE_TYPES_SUCCESS';
export const LOAD_CAMPAIGN_TEMPLATE_TYPES_FAIL = 'campaignList/LOAD_CAMPAIGN_TEMPLATE_TYPES_FAIL';
export const LOAD_CAMPAIGN_TEMPLATE_TYPES = 'campaignList/LOAD_CAMPAIGN_TEMPLATE_TYPES';
// duplicate campaign
export const DUPLICATE_CAMPAIGN = 'campaignList/DUPLICATE_CAMPAIGN';
export const DUPLICATE_CAMPAIGN_SUCCESS = 'campaignList/DUPLICATE_CAMPAIGN_SUCCESS';
export const DUPLICATE_CAMPAIGN_FAIL = 'campaignList/DUPLICATE_CAMPAIGN_FAIL';
export const CLEAR_DUPLICATE_ERROR = 'campaignList/CLEAR_DUPLICATE_ERROR';

export const SET_SELECTED_CAMPAIGN_ID = 'SET_SELECTED_CAMPAIGN_ID';
export const SET_CAMPAIGN_QUERY_DIRTY = 'SET_CAMPAIGN_QUERY_DIRTY';

// Action Creators
export function selectCampaignListPage(page) {
  return {
    type: CAMPAIGN_LIST_PAGE_SELECT,
    page
  };
}

export function setPageParams(queryParams) {
  return {
    type: CAMPAIGN_LIST_SET_QUERY,
    queryParams
  };
}

export function setRecordsPerPage(limit) {
  return {
    type: CAMPAIGN_RECORDS_PER_PAGE_SET,
    limit
  };
}

export function sortColumn(orderBy, sortOrder) {
  return {
    type: CAMPAIGN_LIST_COLUMN_SORT,
    orderBy,
    sortOrder
  };
}

export const setDefaultSortingParams = () => {
  return {
    type: CAMPAIGN_SET_DEFAULT_SORTING
  };
};

export const clearSelectedCampaign = () => {
  return {
    type: CLEAR_CAMPAIGN_DETAILS
  };
};

export const hideError = () => {
  return {
    type: CLEAR_DUPLICATE_ERROR
  };
};

export const setSelectedCampaignId = (selectedCampaignId) => ({
  type: SET_SELECTED_CAMPAIGN_ID,
  selectedCampaignId
});

export const setCampaignList = ({ payload }) => ({
  type: LOAD_CAMPAIGN_LIST_SUCCESS,
  payload
});
export const setCampaignQueryDirty = (payload) => ({
  type: SET_CAMPAIGN_QUERY_DIRTY,
  payload
});

// Actions
export const fetchCampaignList = (queryString = '') =>
  createAction({
    endpoint: `${config.apiHost}${config.endpoints.campaignList}${queryString}`,
    method: 'GET',
    headers: buildHeaders(),
    credentials: 'include',
    types: [LOAD_CAMPAIGN_LIST, LOAD_CAMPAIGN_LIST_SUCCESS, LOAD_CAMPAIGN_LIST_FAIL]
  });

export const fetchCampaignDetails = (campaignLink) =>
  createAction({
    endpoint: `${config.apiHost}${config.endpoints.campaignList}?campaignLinks=${campaignLink}`,
    method: 'GET',
    headers: buildHeaders(),
    credentials: 'include',
    types: [LOAD_CAMPAIGN_DETAILS, LOAD_CAMPAIGN_DETAILS_SUCCESS, LOAD_CAMPAIGN_DETAILS_FAIL]
  });

export const fetchCampaignTemplateTypes = () =>
  createAction({
    endpoint: `${config.apiHost}${config.endpoints.campaignTemplateTypes}`,
    method: 'GET',
    headers: buildHeaders(),
    credentials: 'include',
    types: [
      LOAD_CAMPAIGN_TEMPLATE_TYPES,
      LOAD_CAMPAIGN_TEMPLATE_TYPES_SUCCESS,
      LOAD_CAMPAIGN_TEMPLATE_TYPES_FAIL
    ]
  });

export const duplicateCampaign = (payload) =>
  createAction({
    endpoint: `${config.apiHost}${config.endpoints.campaignDuplicate}`,
    method: 'POST',
    headers: buildHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
    types: [DUPLICATE_CAMPAIGN, DUPLICATE_CAMPAIGN_SUCCESS, DUPLICATE_CAMPAIGN_FAIL]
  });

// Reducer
export function campaignListPageReducer(state = initialState, action) {
  switch (action.type) {
    case CAMPAIGN_LIST_SET_QUERY: {
      const { page, limit, orderBy, sortOrder } = action.queryParams;

      const newState = {
        ...state,
        isLoading: true,
        queryParams: {
          ...state.queryParams,
          page: parseInt(page, 10) || 1,
          limit: limit || config.campaignRecordsPerPageDefault,
          orderBy: orderBy || 'createdAt',
          sortOrder: sortOrder || 'DESC'
        }
      };

      return newState;
    }

    case CAMPAIGN_LIST_COLUMN_SORT: {
      const newState = {
        ...state,
        queryParams: {
          ...state.queryParams,
          orderBy: action.orderBy,
          sortOrder: action.sortOrder
        }
      };

      return newState;
    }

    case '@@router/LOCATION_CHANGE': {
      const shouldDeleteQueryParams =
        action.payload && action.payload.pathname && action.payload.pathname !== '/campaign';

      const newState = {
        ...state,
        queryParams: {}
      };

      return shouldDeleteQueryParams ? newState : state;
    }

    case CAMPAIGN_SET_DEFAULT_SORTING: {
      const newState = {
        ...state,
        queryParams: {
          page: 1,
          limit: config.campaignRecordsPerPageDefault,
          orderBy: 'createdAt',
          sortOrder: 'DESC'
        }
      };

      return newState;
    }

    case CAMPAIGN_LIST_PAGE_SELECT: {
      const newState = {
        ...state,
        queryParams: {
          ...state.queryParams,
          page: action.page
        }
      };

      return newState;
    }

    case CAMPAIGN_RECORDS_PER_PAGE_SET: {
      // when changing the # of records per page, we will change the current
      // selected page to one that contains the previous top result displayed on the page
      const prevTopResult = state.meta.offset + 1;
      const newLimit = action.limit;
      const newPageCount = calculatePageCount(state.meta.count, newLimit);

      const pageContainingPrevTopResult = Math.ceil(prevTopResult / newLimit);

      const newState = {
        ...state,
        queryParams: {
          ...state.queryParams,
          limit: newLimit,
          page: pageContainingPrevTopResult
        },
        meta: {
          ...state.meta,
          pageCount: newPageCount
        }
      };

      return newState;
    }

    case LOAD_CAMPAIGN_LIST_SUCCESS: {
      const newState = {
        ...state,
        isLoading: false,
        errorMessage: '',
        data: action.payload.data,
        meta: {
          ...action.payload.meta,
          pageCount: calculatePageCount(action.payload.meta.count, action.payload.meta.limit)
        }
      };

      return newState;
    }
    case SET_CAMPAIGN_QUERY_DIRTY: {
      const newState = {
        ...state,
        refetch: !!action.payload
      };

      return newState;
    }
    case LOAD_CAMPAIGN_LIST: {
      const newState = {
        ...state,
        isLoading: true,
        errorMessage: ''
      };
      return newState;
    }

    case LOAD_CAMPAIGN_LIST_FAIL: {
      const newState = {
        ...state,
        isLoading: false,
        errorMessage: action.payload.response.message
      };
      return newState;
    }

    case LOAD_CAMPAIGN_DETAILS_SUCCESS: {
      const newState = {
        ...state,
        isSelectedCampaignLoading: false,
        selectedCampaignErrorMessage: '',
        selectedCampaign: action.payload.data[0],
        meta: {
          ...action.payload.meta,
          pageCount: calculatePageCount(action.payload.meta.count, action.payload.meta.limit)
        }
      };

      return newState;
    }

    case LOAD_CAMPAIGN_DETAILS: {
      const newState = {
        ...state,
        isSelectedCampaignLoading: true,
        selectedCampaignErrorMessage: ''
      };
      return newState;
    }

    case LOAD_CAMPAIGN_DETAILS_FAIL: {
      const newState = {
        ...state,
        isSelectedCampaignLoading: false,
        selectedCampaignErrorMessage: action.payload.response.message
      };
      return newState;
    }

    case CLEAR_CAMPAIGN_DETAILS: {
      const newState = {
        ...state,
        selectedCampaign: null,
        isSelectedCampaignLoading: false,
        selectedCampaignErrorMessage: ''
      };
      return newState;
    }

    case LOAD_CAMPAIGN_TEMPLATE_TYPES_FAIL: {
      const newState = {
        ...state,
        nextGenCampaignTemplates: [],
        loadCampaignTemplatesError: true
      };
      return newState;
    }

    case LOAD_CAMPAIGN_TEMPLATE_TYPES_SUCCESS: {
      const newState = {
        ...state,
        nextGenCampaignTemplates: action.payload,
        loadCampaignTemplatesError: false
      };
      return newState;
    }

    case DUPLICATE_CAMPAIGN_SUCCESS: {
      const newState = {
        ...state,
        hasDuplicateCampaignError: false
      };
      return newState;
    }

    case DUPLICATE_CAMPAIGN_FAIL: {
      const newState = {
        ...state,
        hasDuplicateCampaignError: true
      };
      return newState;
    }

    case CLEAR_DUPLICATE_ERROR: {
      const newState = {
        ...state,
        hasDuplicateCampaignError: false
      };
      return newState;
    }

    case SET_SELECTED_CAMPAIGN_ID: {
      return {
        ...state,
        selectedCampaignId: action.selectedCampaignId
      };
    }

    default:
      return state;
  }
}

// Selectors
export const getCampaignListState = createSelector(
  (state) => state,
  (state) => state.get('campaignList')
);
export const getCampaignQueryDirty = createSelector(getCampaignListState, (campaignListState) =>
  deep(campaignListState, 'refetch')
);

export const getCampaignQueryParams = createSelector(getCampaignListState, (campaignListState) =>
  deep(campaignListState, 'queryParams')
);

export const getSortedColumn = createSelector(getCampaignQueryParams, (queryParams) =>
  deep(queryParams, 'orderBy')
);

export const getSortOrder = createSelector(getCampaignQueryParams, (queryParams) =>
  deep(queryParams, 'sortOrder')
);

export const getCurrentPage = createSelector(
  getCampaignQueryParams,
  (queryParams) => deep(queryParams, 'page') || 1
);

export const getCampaignMeta = createSelector(getCampaignListState, (campaignListState) =>
  deep(campaignListState, 'meta')
);

export const getPageCount = createSelector(getCampaignMeta, (campaignMeta) =>
  deep(campaignMeta, 'pageCount')
);

export const getRecordsPerPage = createSelector(getCampaignMeta, (campaignMeta) =>
  deep(campaignMeta, 'limit')
);

export const getListApiQueryParams = createSelector(getCampaignQueryParams, (queryParams) => {
  if (Object.keys(queryParams).length > 0) {
    const queryParamsCopy = {
      ...queryParams,
      offset: parseInt(queryParams.page - 1, 10) * parseInt(queryParams.limit, 10),
      page: undefined // remove from object so it does not get serialized
    };

    return `?${qs.stringify(queryParamsCopy, { skipNulls: true })}`;
  }

  return '';
});

export const getListBrowserQueryParams = createSelector(
  getCampaignQueryParams,
  (queryParams) => queryParams
);

export const getCampaignList = createSelector(getCampaignListState, (campaignListState) =>
  deep(campaignListState, 'data')
);

export const getHasNoCampaigns = createSelector(
  getCampaignList,
  (data) => data.length === 0 || data.length === undefined
);

export const getIsCampaignListLoading = createSelector(
  getCampaignListState,
  (state) => state.isLoading
);

export const getHasCampaigns = createSelector(
  getCampaignListState,
  (campaignListState) => campaignListState.data.length !== 0
);

export const getAllCampaignNamesAsSet = createSelector(
  getCampaignList,
  (data) => new Set(data.map((c) => c.name))
);

export const getTotalCampaignsCount = createSelector(getCampaignMeta, (meta) =>
  deep(meta, 'count')
);

export const getSelectedCampaign = createSelector(getCampaignListState, (campaignListState) =>
  deep(campaignListState, 'selectedCampaign')
);

export const getCampaignTemplateTypes = createSelector(
  getCampaignListState,
  (campaignListState) => {
    // get v2 templates fetched from api
    const nextGenTemplates = deep(campaignListState, 'nextGenCampaignTemplates');

    // parse into format to populate radio button in create campaign modal
    const parsedNextGenTemplates = nextGenTemplates.map(({ templateName, description, id }) => ({
      label: templateName,
      info: description,
      templateId: id,
      campaignTemplateType: 3,
      nextGen: true
    }));

    const allTemplates = parsedNextGenTemplates;

    // join the lists and set unique values for each
    const fullTemplateList = allTemplates.map((template, idx) => ({
      ...template,
      value: idx
    }));

    return fullTemplateList;
  }
);

export const getLoadCampaignTemplatesError = createSelector(
  getCampaignListState,
  (campaignListState) => deep(campaignListState, 'loadCampaignTemplatesError')
);

export const getHasDuplicateCampaignError = createSelector(
  getCampaignListState,
  (campaignListState) => deep(campaignListState, 'hasDuplicateCampaignError')
);

export const getSelectedCampaignId = createSelector(
  getCampaignListState,
  (campaignListState) => campaignListState?.selectedCampaignId
);



// WEBPACK FOOTER //
// ./src/redux/modules/campaignList.js